import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import WebhookManager from "./WebhookManager";
import webhookService from "../../../lib/webhookService";
import { useAuth } from "../../../contexts/AuthContext";

const IntegrationStatus = () => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState("status");
  const [webhookStats, setWebhookStats] = useState({
    total: 0,
    active: 0,
    failed: 0,
    recentActivity: [],
  });
  const [loading, setLoading] = useState(false);

  // Load webhook stats when component mounts or user changes
  useEffect(() => {
    if (user?.id) {
      loadWebhookStats();
    }
  }, [user?.id]);

  const loadWebhookStats = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      // Get webhook statistics
      const stats = await webhookService.getWebhookStats(user.id);

      console.log(stats)
      // Get recent webhooks for activity display
      const { data: webhooks } = await webhookService.getWebhooks(user.id);
      const recentActivity =
        webhooks?.slice(0, 3).map((webhook) => ({
          id: webhook.id,
          url: webhook.url,
          status: webhook.status,
          lastTriggered: webhook.last_triggered,
        })) || [];

      setWebhookStats({
        ...stats.data,
        recentActivity,
      });
    } catch (error) {
      console.error("Failed to load webhook stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const integrations = [
    {
      id: 1,
      name: "REST API",
      type: "api",
      status: "connected",
      lastSync: "2025-09-14 12:35:00",
      requests: 15420,
      health: "healthy",
    },
    {
      id: 2,
      name: "WebSocket Connection",
      type: "websocket",
      status: "connected",
      lastSync: "2025-09-14 12:40:00",
      requests: 8950,
      health: "healthy",
    },
    {
      id: 3,
      name: "Push Notifications",
      type: "push",
      status: "disconnected",
      lastSync: "2025-09-13 18:22:00",
      requests: 0,
      health: "error",
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "connected":
      case "active":
        return "bg-accent/10 text-accent border-accent/20";
      case "disconnected":
      case "failed":
        return "bg-destructive/10 text-destructive border-destructive/20";
      case "warning":
        return "bg-warning/10 text-warning border-warning/20";
      default:
        return "bg-muted text-muted-foreground border-border";
    }
  };

  const getHealthColor = (health) => {
    switch (health) {
      case "healthy":
        return "text-accent";
      case "warning":
        return "text-warning";
      case "error":
        return "text-destructive";
      default:
        return "text-muted-foreground";
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "api":
        return "Globe";
      case "websocket":
        return "Zap";
      case "webhook":
        return "Webhook";
      case "push":
        return "Bell";
      default:
        return "Settings";
    }
  };

  return (
    <div className="space-y-6">
      {/* Section Navigation */}
      <div className="flex items-center space-x-4 border-b border-border pb-4">
        <button
          onClick={() => {
            setActiveSection("status");
            loadWebhookStats(); // Refresh webhook stats when switching to status tab
          }}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeSection === "status"
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          }`}
        >
          <Icon name="Activity" size={16} className="inline mr-2" />
          Integration Status
        </button>
        <button
          onClick={() => setActiveSection("webhooks")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeSection === "webhooks"
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          }`}
        >
          <Icon name="Webhook" size={16} className="inline mr-2" />
          Webhook Management
        </button>
      </div>

      {activeSection === "status" ? (
        <div className="space-y-6">
          {/* Integration Status */}
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">
                Integration Status
              </h2>
              <Button
                variant="outline"
                size="sm"
                iconName="RefreshCw"
                iconPosition="left"
              >
                Refresh Status
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {integrations?.map((integration) => (
                <div
                  key={integration?.id}
                  className="p-4 bg-muted/30 rounded-lg border border-border"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Icon
                          name={getTypeIcon(integration?.type)}
                          size={16}
                          className="text-primary"
                        />
                      </div>
                      <div>
                        <h3 className="font-medium text-foreground">
                          {integration?.name}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          {integration?.type?.toUpperCase()}
                        </p>
                      </div>
                    </div>
                    <div
                      className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(integration?.status)}`}
                    >
                      {integration?.status}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Health:</span>
                      <div className="flex items-center space-x-1 mt-1">
                        <Icon
                          name="Activity"
                          size={12}
                          className={getHealthColor(integration?.health)}
                        />
                        <span
                          className={`text-xs font-medium ${getHealthColor(integration?.health)}`}
                        >
                          {integration?.health}
                        </span>
                      </div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Requests:</span>
                      <p className="text-foreground mt-1">
                        {integration?.requests?.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-border">
                    <span className="text-xs text-muted-foreground">
                      Last sync: {integration?.lastSync}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Webhook Summary */}
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">
                Webhook Summary
              </h2>
              <Button
                variant="outline"
                size="sm"
                iconName="Settings"
                iconPosition="left"
                onClick={() => setActiveSection("webhooks")}
              >
                Manage Webhooks
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-muted/30 rounded-lg border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Total Webhooks
                    </p>
                    <p className="text-2xl font-bold text-foreground">
                      {loading ? "..." : webhookStats.total}
                    </p>
                  </div>
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Icon name="Webhook" size={20} className="text-primary" />
                  </div>
                </div>
              </div>

              <div className="p-4 bg-muted/30 rounded-lg border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Active</p>
                    <p className="text-2xl font-bold text-accent">
                      {loading ? "..." : webhookStats.active}
                    </p>
                  </div>
                  <div className="p-3 bg-accent/10 rounded-lg">
                    <Icon
                      name="CheckCircle"
                      size={20}
                      className="text-accent"
                    />
                  </div>
                </div>
              </div>

              <div className="p-4 bg-muted/30 rounded-lg border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Failed</p>
                    <p className="text-2xl font-bold text-destructive">
                      {loading ? "..." : webhookStats.failed}
                    </p>
                  </div>
                  <div className="p-3 bg-destructive/10 rounded-lg">
                    <Icon
                      name="AlertCircle"
                      size={20}
                      className="text-destructive"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Webhook Activity */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-3">
                Recent Activity
              </h3>
              <div className="space-y-2">
                {loading ? (
                  <div className="text-center py-4">
                    <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
                  </div>
                ) : webhookStats.recentActivity.length > 0 ? (
                  webhookStats.recentActivity.map((webhook) => (
                    <div
                      key={webhook.id}
                      className="flex items-center justify-between p-3 bg-muted/30 rounded border border-border"
                    >
                      <div className="flex items-center space-x-3">
                        <Icon
                          name="Webhook"
                          size={14}
                          className="text-muted-foreground"
                        />
                        <span className="text-sm text-foreground font-mono">
                          {webhook.url}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-xs text-muted-foreground">
                          {webhook.lastTriggered
                            ? new Date(webhook.lastTriggered).toLocaleString()
                            : "Never"}
                        </span>
                        <div
                          className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(webhook.status)}`}
                        >
                          {webhook.status}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-muted-foreground">
                      No webhooks configured yet
                    </p>
                  </div>
                )}
              </div>
              <div className="mt-4 text-center">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ArrowRight"
                  iconPosition="right"
                  onClick={() => setActiveSection("webhooks")}
                >
                  View All Webhooks
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <WebhookManager />
      )}
    </div>
  );
};

export default IntegrationStatus;
